import { PushNotifications } from "@capacitor/push-notifications";

import { isNativeApp, nativePlatform } from "./native";

// Remembered so sign-out can delete the exact token this device registered.
const TOKEN_KEY = "zw_push_token";

/**
 * Ask for permission, register with APNs/FCM and bind the token to the
 * logged-in admin. Called fire-and-forget after login — push is a
 * nice-to-have and must never interfere with signing in.
 */
export const registerDevicePush = async (store: {
  postData: (queryString: string, data: any) => Promise<any>;
}): Promise<void> => {
  if (!isNativeApp()) return;

  try {
    let permission = await PushNotifications.checkPermissions();
    if (permission.receive === "prompt") {
      permission = await PushNotifications.requestPermissions();
    }
    if (permission.receive !== "granted") return;

    const handles: Array<{ remove: () => Promise<void> }> = [];
    try {
      const token = await new Promise<string>((resolve, reject) => {
        PushNotifications.addListener("registration", (t) => resolve(t.value)).then((h) => handles.push(h));
        PushNotifications.addListener("registrationError", (e) => reject(new Error(JSON.stringify(e)))).then((h) =>
          handles.push(h)
        );
        PushNotifications.register().catch(reject);
      });

      localStorage.setItem(TOKEN_KEY, token);
      await store.postData("devices", { token, platform: nativePlatform() });
    } finally {
      handles.forEach((h) => h.remove().catch(() => {}));
    }
  } catch (err) {
    console.error("Push registration failed:", err);
  }
};

/** Delete this device's token server-side. Must run while still authenticated. */
export const unregisterDevicePush = async (store: {
  deleteData: (queryString: string) => Promise<any>;
}): Promise<void> => {
  if (!isNativeApp()) return;

  const token = localStorage.getItem(TOKEN_KEY);
  if (!token) return;

  try {
    await store.deleteData(`devices/${encodeURIComponent(token)}`);
    localStorage.removeItem(TOKEN_KEY);
  } catch (err) {
    console.error("Push unregister failed:", err);
  }
};
